import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllStarRatings } from '../../apis/index';
import { getAllChatHistoryCached } from '../../actions/dataConsoleAction';
import DashLoader from '../../components/DashLoader';
import '../../styles/DatasetDashboard.css';
import '../../styles/Dashboard.css';


const DatasetDashboard = () => {
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState('');
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);
  const [isOpen, setIsOpen] = useState(false); // Track dropdown open state
  
  // Move state variables for ratings to the top level
  const [ratingsData, setRatingsData] = useState([]);
  const [totalRatedChats, setTotalRatedChats] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [totalAnswers, setTotalAnswers] = useState(0);
  const [accurateAnswers, setAccurateAnswers] = useState(0);
  const [notAccurateAnswers, setNotAccurateAnswers] = useState(0);
  const [totalChats, settotalChats] = useState([]);
  const [allChatHistory, setAllChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Fetching data...');
  const hasFetchedRef = useRef(false);

  const dispatch = useDispatch();
  const allChatHistoryCache = useSelector((state) => state.allChatHistoryRed);
  
  const fetchDatasets = async () => {
    try {
      setIsLoading(true);
      setLoadingMessage('Fetching chat history...');
      // Use Redux-cached chat history (fetches only once across dashboards)
      const chatHistoryData = await dispatch(getAllChatHistoryCached());
      setAllChatHistory(chatHistoryData);
  
      // Use a Map to store unique dataset names, and for duplicates,
      // keep the one with the higher datasetId (assumed to be the active dataset)
      const datasetMap = new Map();
  
      chatHistoryData.forEach((item) => {
        const { datasetName, datasetId } = item;
        // If the dataset name already exists, compare IDs
        if (datasetMap.has(datasetName)) {
          const existingId = datasetMap.get(datasetName);
          // Convert to numbers if necessary to compare correctly
          if (Number(datasetId) > Number(existingId)) {
            datasetMap.set(datasetName, datasetId);
          }
        } else {
          datasetMap.set(datasetName, datasetId);
        }
      });
  
      // Convert the Map back to an array of objects, where each object represents
      // a unique dataset name and its active datasetId.
      const datasets = Array.from(datasetMap, ([datasetName, datasetId]) => ({
        datasetId,
        datasetName,
      }));
  
      setDatasets(datasets);
  
      // Automatically select the first dataset after loading
      if (datasets.length > 0) {
        setSelectedDataset(datasets[0].datasetName);
        setSelectedDatasetId(datasets[0].datasetId);
      }
    } catch (error) {
      console.error('Error fetching datasets', error);
    } finally {
      setIsLoading(false);
    }
  };
  


  const fetchRatingsData = async (datasetID) => {
    try {
      setIsLoading(true);
      setLoadingMessage('Building dashboard...');
      const ratingResponse = await fetchAllStarRatings();
  
      const ratingData = ratingResponse.data.filter((item) => item.datasetId === datasetID);
      const chatHistoryData = allChatHistory.filter((item) => item.datasetId === datasetID);
      settotalChats(chatHistoryData.length);
      // Extract chatIds, ratings and datasets
      const chatIds = new Set(ratingData.map(item => item.chatId)); // Use Set for faster lookup
      const ratings = ratingData.map(item => item.rating);
      setTotalRatedChats(ratings.length);
      

      // Calculate accurate and not accurate answers
      const accurate = ratings.filter(rating => rating >= 3).length;
      const notAccurate = ratings.length - accurate;

      setAccurateAnswers(accurate);
      setNotAccurateAnswers(notAccurate);
      setTotalAnswers(ratings.length);
  
      // Map chatId to userId using chatHistoryData
      const userIds = chatHistoryData
        .filter(record => chatIds.has(record.id)) // Use Set for filtering
        .map(record => record.userId);
  
      // Calculate unique users
      const uniqueUserIds = [...new Set(userIds)];
      setTotalUsers(uniqueUserIds.length);

      
  
      // Calculate average rating
      const totalRatings = ratings.reduce((sum, rating) => sum + rating, 0);
      const avgRating = ratings.length > 0 ? totalRatings / ratings.length : 0; // Handle division by zero
      setAverageRating(avgRating);
  
      // Group and process ratings for breakdown display
      const ratingCounts = ratings.reduce((acc, rating) => {
        acc[rating] = (acc[rating] || 0) + 1; // Count each rating
        return acc;
      }, {});
  
      const ratingBreakdown = [1, 2, 3, 4, 5].map(star => ({
        label: `${star} star`,
        percentage: calcPercentage(ratings, star),
        count: ratingCounts[star] || 0, // Use counted values
      }));
  
      setRatingsData(ratingBreakdown);
      
    } catch (error) {
      console.error('Error fetching ratings data:', error);
      
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions to calculate percentages
  const calcPercentage = (ratings, star) => {
    const count = ratings.filter(rating => rating === star).length;
    return (count / ratings.length) * 100;
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchDatasets();
  }, []);

  useEffect(() => {
    if (selectedDatasetId) {
      // Reset state before fetching new data
      setTotalAnswers(0);
      setAccurateAnswers(0);
      setNotAccurateAnswers(0);
      setRatingsData([]);
      setTotalUsers(0);
      setAverageRating(0);

      fetchRatingsData(selectedDatasetId); // Call API when selectedDatasetId changes
    }
  }, [selectedDatasetId]); // Dependency array includes selectedDatasetId

  const handleDatasetChange = (event) => {
    const selected = datasets.find(dataset => dataset.datasetName === event.target.value); // Find dataset by name
    setSelectedDataset(selected.datasetName); // Set the selected dataset name
    setSelectedDatasetId(selected.datasetId); // Set the selected dataset ID
    setIsOpen(false); // Close the dropdown after selection
  };

  return (
    //add overflow-y as auto
    <div class="dataset_dashboard">

      {isLoading && (
        <div className="dd-loader-overlay" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <DashLoader />
        </div>
      )}

      {/* Dataset dropdown */}
      <div className="dd-dropdown">
        <select
          value={selectedDataset}
          onChange={handleDatasetChange}
         // onFocus={() => setIsOpen(true)} // Open dropdown on focus
         // onBlur={() => setIsOpen(false)} // Close dropdown on blur
        >
          <option value="dataset" disabled>Select Dataset</option>
          {datasets.map((dataset) => (
            <option key={dataset.datasetId} value={dataset.datasetName}>
              {dataset.datasetName}
            </option>
          ))}
        </select>
        </div>

      {selectedDataset && (
        <div className="dd-selected-dataset">
          <div className="dashboard">
          <div class="dashboard-cards">
            <div className="chart-container">
              {/* Render experience ratio here */}
              <div className="card_dashboard experience-ration">
                {/* Title and total rating */}
                <h3 className="chart-title">User experience ratio</h3>
                <p className="card-subtitle">{totalUsers} user(s) rated {totalRatedChats} chat(s) out of {totalChats} chat(s) in "{selectedDataset}" dataset</p>
    
                {/* Star Rating Section */}
                <div className="star-rating-dashboard">
                  <span className="stars">
                    {/* Dynamically render stars based on average rating */}
                    {'★'.repeat(Math.floor(averageRating))}
                    {'☆'.repeat(5 - Math.floor(averageRating))}
                  </span>
                  <span className="rating-score">{averageRating.toFixed(1)} out of 5</span>
                </div>
    
                {/* Ratings Breakdown */}
                <div className="ratings-breakdown">
                  {ratingsData.map((rating) => (
                    <div className="rating-row" key={rating.label}>
                      <span className="rating-label">{rating.label}</span>
                      <div className="rating-bar">
                        <div
                          className="rating-bar-filled"
                          style={{ width: `${rating.count > 0 ? rating.percentage : 0}%`, backgroundColor: '#FFA500' }}
                        ></div>
                      </div>
                      <span className="rating-percentage" style={{ marginRight: '10px' }}>
        {/* <span className="label">Rating Percentage: </span> */}
        <span title={`Rating Percentage: ${rating.percentage.toFixed(1)}%`}>
          {rating.count > 0 ? rating.percentage.toFixed(1) : '0.0'}%
        </span>
      </span>
      <span className="rating-count">
        {/* <span className="label">Chat(s) Rated: </span> */}
        <span title={`No of ratings: ${rating.count}`}>
          ({rating.count})
        </span>
      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </div>
            <div class="dashboard-cards">
            <div className="chart-container">
                {/* New Playground Accuracy Card */}
              <div className="card_dashboard playground-accuracy">
                <h3 className="chart-title">Star Rating Count</h3>
                <p className="card-subtitle" style={{paddingBottom:'10px'}}>Total number of chat(s) rated - {totalAnswers}</p>

                {/* Accurate Answers Progress Bar */}
                <div className="progress-bar">
                    <div className="progress-bar-filled" style={{ width: `${totalAnswers > 0 ? (accurateAnswers / totalAnswers) * 100 : 0}%`, backgroundColor: '#00BFFF' }}></div>
                  </div>
                <div className="progress-bar-container">
                  <div className="progress-label">
                  Count of 3/4/5 Star Ratings {totalAnswers > 0 
                      ? ((accurateAnswers / totalAnswers) * 100).toFixed(1) 
                      : '0.0'}%  ({accurateAnswers} Chat(s) rated)
                  </div>
                  
                </div>
                {/* Not Accurate Answers Progress Bar */}
                <div className="progress-bar">
                    <div className="progress-bar-filled" style={{ width: `${totalAnswers > 0 ? (notAccurateAnswers / totalAnswers) * 100 : 0}%`, backgroundColor: '#4B0082' }}></div>
                  </div>
                <div className="progress-bar-container">
                  <div className="progress-label">
                  Count of 1/2 Star Ratings {totalAnswers > 0 
                      ? ((notAccurateAnswers / totalAnswers) * 100).toFixed(1) 
                      : '0.0'}%  ({notAccurateAnswers} Chat(s) rated )
                  </div>
                 
                  </div>
                  </div>
                  </div>
                  </div>
          </div>
         </div>
      )}
    </div>
  );
};

export default DatasetDashboard;
